import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

import { useAuth } from "../../auth/AuthContext";
import { useBrand } from "../../brand/BrandContext";
import { deriveCustomerFirstName } from "../../lib/customerNames";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

type LeadStatus =
  | "ready"
  | "queued"
  | "sent"
  | "booked"
  | "pipeline"
  | "existing_contact"
  | "opted_out"
  | "bad_phone"
  | "duplicate"
  | "failed"
  | "suppressed"
  | "blocked";
type Stage = "waiting" | "parsed" | "previewed";
type ApiState = "idle" | "loading" | "ready" | "error";
type ActionState = "idle" | "loading" | "ready" | "error";

type SpeedToLeadLead = {
  id: string;
  dbId: number | null;
  name: string;
  phone: string;
  email: string;
  zip: string;
  attempts: number | null;
  lastTouch: string;
  sourceStatus: string;
  status: LeadStatus;
};

type SpeedToLeadApiLead = {
  id: number | string;
  wfy_lead_id: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  zip: string | null;
  attempts: number | null;
  lead_date: string | null;
  last_touch: string | null;
  source_status: string | null;
  status: LeadStatus;
};

type SpeedToLeadTotals = {
  total: number;
  imported: number;
  ready: number;
  queued: number;
  sent: number;
  booked: number;
  in_pipeline: number;
  existing_contact: number;
  opted_out: number;
  bad_phone: number;
  duplicates: number;
  suppressed: number;
  failed: number;
};

type DeliveryMode = {
  mode: "manual";
  manual_send_available: boolean;
  auto_mode_available: boolean;
  auto_mode_enabled: boolean;
};

type SpeedToLeadTemplate = {
  id: number | null;
  code: string;
  name: string;
  body: string;
  source: string;
  version: number | null;
  updated_at: string | null;
  updated_by: string | null;
  validation: {
    ok: boolean;
    errors: string[];
  };
};

type SpeedToLeadQueue = {
  totals: SpeedToLeadTotals;
  latest_batch: {
    upload_batch_id: string;
    last_created_at: string | null;
    total: number;
    imported: number;
    in_pipeline: number;
    existing_contact: number;
    opted_out: number;
    bad_phone: number;
    duplicates: number;
  } | null;
  leads: SpeedToLeadApiLead[];
};

type SpeedToLeadSummary = {
  queue: SpeedToLeadQueue;
  delivery?: DeliveryMode;
  template?: SpeedToLeadTemplate;
};

type ApplyQueueOptions = {
  preserveSelection?: boolean;
};

type ImportSummary = {
  batch_id: string;
  parsed: number;
  imported: number;
  duplicates: number;
  opted_out: number;
  bad_phone: number;
  in_pipeline: number;
  existing_contact: number;
  not_follow_up: number;
};

const sampleLeads: SpeedToLeadLead[] = [
  {
    id: "WFY-9842",
    dbId: null,
    name: "Maria Lopez",
    phone: "(561) 555-0134",
    email: "maria@example.test",
    zip: "33458",
    attempts: 4,
    lastTouch: "2 days ago",
    sourceStatus: "Need to Follow Up 2",
    status: "ready",
  },
  {
    id: "WFY-9849",
    dbId: null,
    name: "David Chen",
    phone: "(561) 555-0198",
    email: "david@example.test",
    zip: "33410",
    attempts: 3,
    lastTouch: "4 days ago",
    sourceStatus: "Need to Follow Up 3",
    status: "ready",
  },
  {
    id: "WFY-9867",
    dbId: null,
    name: "Sofia Ramirez",
    phone: "(561) 555-0144",
    email: "sofia@example.test",
    zip: "33477",
    attempts: 5,
    lastTouch: "today",
    sourceStatus: "No Answer",
    status: "queued",
  },
  {
    id: "WFY-9851",
    dbId: null,
    name: "Patricia Green",
    phone: "(772) 555-0122",
    email: "patricia@example.test",
    zip: "33477",
    attempts: 5,
    lastTouch: "6 days ago",
    sourceStatus: "Need to Follow Up 3",
    status: "pipeline",
  },
  {
    id: "WFY-9857",
    dbId: null,
    name: "Ronald Davis",
    phone: "(561) 555-0175",
    email: "ronald@example.test",
    zip: "33418",
    attempts: 2,
    lastTouch: "8 days ago",
    sourceStatus: "No Answer",
    status: "opted_out",
  },
  {
    id: "WFY-9863",
    dbId: null,
    name: "Elena Martin",
    phone: "(561) 555-0117",
    email: "elena@example.test",
    zip: "33408",
    attempts: 3,
    lastTouch: "9 days ago",
    sourceStatus: "Bad Phone",
    status: "bad_phone",
  },
  {
    id: "WFY-9871",
    dbId: null,
    name: "Brenda Wilson",
    phone: "(561) 555-0164",
    email: "brenda@example.test",
    zip: "33401",
    attempts: 4,
    lastTouch: "yesterday",
    sourceStatus: "Need to Follow Up 2",
    status: "sent",
  },
  {
    id: "WFY-9873",
    dbId: null,
    name: "Howard Miller",
    phone: "(561) 555-0182",
    email: "howard@example.test",
    zip: "33469",
    attempts: 3,
    lastTouch: "yesterday",
    sourceStatus: "Need to Follow Up 2",
    status: "booked",
  },
];

const placeholderLead: SpeedToLeadLead = {
  id: "placeholder",
  dbId: null,
  name: "there",
  phone: "",
  email: "",
  zip: "",
  attempts: null,
  lastTouch: "unknown",
  sourceStatus: "",
  status: "blocked",
};

const speedToLeadTemplateCode = "speed_to_lead_booking_sms_v1";
const defaultSmsTemplate = [
  "Hi {{first_name}}, {{brand_name}} here. We tried to reach you about your free estimate.",
  "You can book online here: {{booking_url}} or call {{call_center_phone}}.",
].join(" ");

function statusMeta(status: LeadStatus): { label: string; variant: "green" | "amber" | "red" | "slate" | "default" } {
  if (status === "ready") return { label: "Ready", variant: "green" };
  if (status === "queued") return { label: "Queued for window", variant: "default" };
  if (status === "sent") return { label: "SMS sent", variant: "green" };
  if (status === "booked") return { label: "Booked online", variant: "green" };
  if (status === "pipeline") return { label: "Already in pipeline", variant: "amber" };
  if (status === "existing_contact") return { label: "Known contact", variant: "amber" };
  if (status === "opted_out") return { label: "Opted out", variant: "red" };
  if (status === "bad_phone") return { label: "Bad phone", variant: "slate" };
  if (status === "duplicate") return { label: "Duplicate", variant: "slate" };
  if (status === "failed") return { label: "Failed", variant: "red" };
  if (status === "suppressed") return { label: "Suppressed", variant: "slate" };
  return { label: "Blocked", variant: "slate" };
}

function brandShortName(name: string) {
  return name.toLowerCase().startsWith("sir grout") ? "Sir Grout" : name;
}

function renderSmsTemplate(template: string, values: Record<string, string>) {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key) => values[key] ?? "").trim();
}

function mapApiLead(lead: SpeedToLeadApiLead): SpeedToLeadLead {
  const id = String(lead.wfy_lead_id || lead.id);
  return {
    id,
    dbId: Number.isFinite(Number(lead.id)) ? Number(lead.id) : null,
    name: lead.name || "Unknown",
    phone: lead.phone || "",
    email: lead.email || "",
    zip: lead.zip || "",
    attempts: lead.attempts,
    lastTouch: formatTouch(lead.last_touch || lead.lead_date),
    sourceStatus: lead.source_status || "",
    status: normalizeLeadStatus(lead.status),
  };
}

function normalizeLeadStatus(value: string): LeadStatus {
  const allowed: LeadStatus[] = [
    "ready",
    "queued",
    "sent",
    "booked",
    "pipeline",
    "existing_contact",
    "opted_out",
    "bad_phone",
    "duplicate",
    "failed",
    "suppressed",
    "blocked",
  ];
  return allowed.includes(value as LeadStatus) ? (value as LeadStatus) : "blocked";
}

function formatTouch(value: string | null) {
  if (!value) return "unknown";
  const normalized = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

async function fileToBase64(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export function SpeedToLeadContent() {
  const { accessToken } = useAuth();
  const brand = useBrand();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("waiting");
  const [selectedId, setSelectedId] = useState(sampleLeads[0].id);
  const [leads, setLeads] = useState<SpeedToLeadLead[]>([]);
  const [queue, setQueue] = useState<SpeedToLeadQueue | null>(null);
  const [delivery, setDelivery] = useState<DeliveryMode>({
    mode: "manual",
    manual_send_available: false,
    auto_mode_available: false,
    auto_mode_enabled: false,
  });
  const [smsTemplate, setSmsTemplate] = useState<SpeedToLeadTemplate | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [apiState, setApiState] = useState<ApiState>("idle");
  const [apiError, setApiError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<ActionState>("idle");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  const showSampleLeads = apiState === "idle" || apiState === "loading";
  const displayLeads = leads.length > 0 ? leads : showSampleLeads ? sampleLeads : [];
  const selectedLead = displayLeads.find((lead) => lead.id === selectedId) ?? displayLeads[0] ?? placeholderLead;
  const brandName = brand.brandName || "Current tenant";
  const callCenterPhone = brand.callbackPhone || brand.gmPhone || "tenant call center";
  const businessTimezone = brand.timezone || "tenant local time";
  const bookingUrl = brand.bookingLink.trim();
  const hasBookingUrl = /^https?:\/\//i.test(bookingUrl);
  const bookingUrlLabel = hasBookingUrl ? bookingUrl : "Missing tenant booking link";
  const liveGates = [
    ["Booking URL", bookingUrlLabel],
    ["Call center fallback", callCenterPhone],
    ["SMS template", smsTemplate?.validation.ok === false ? "blocked until template is corrected" : "tenant version with STOP guardrail"],
    ["Send window", `09:00-19:00 ${businessTimezone}, no Sunday`],
    ["Opt-out", "checked at import and send time"],
    ["Known contacts", "phone or email checked before preview/send"],
    ["Quote pipeline", "checked at import and send time"],
  ];
  const sendableLeads = displayLeads.filter((lead) => lead.dbId && lead.status === "ready");
  const selectedSendableCount = selectedLeadIds.length;
  const allSendableSelected = sendableLeads.length > 0 && sendableLeads.every((lead) => lead.dbId && selectedLeadIds.includes(lead.dbId));
  const liveSendLocked = !delivery.manual_send_available;

  const counts = useMemo(() => {
    const totals = queue?.totals;
    if (totals) {
      return {
        total: totals.total,
        sendable: totals.ready,
        blocked: totals.in_pipeline + (totals.existing_contact || 0) + totals.opted_out + totals.bad_phone + totals.duplicates + totals.failed + (totals.suppressed || 0),
        sent: totals.sent,
        booked: totals.booked,
      };
    }
    const sendable = displayLeads.filter((lead) => lead.status === "ready").length;
    const blocked = displayLeads.filter((lead) => ["pipeline", "existing_contact", "opted_out", "bad_phone", "duplicate", "failed", "suppressed", "blocked"].includes(lead.status)).length;
    const sent = displayLeads.filter((lead) => lead.status === "sent").length;
    const booked = displayLeads.filter((lead) => lead.status === "booked").length;
    return { total: displayLeads.length, sendable, blocked, sent, booked };
  }, [displayLeads, queue]);

  const batchName = fileName || "Drop WebFindYou .xls export";
  const batchId = importSummary?.batch_id || queue?.latest_batch?.upload_batch_id || "No batch imported yet";
  const activeTemplateBody = smsTemplate?.body || defaultSmsTemplate;
  const smsBody = hasBookingUrl
    ? renderSmsTemplate(activeTemplateBody, {
      booking_url: bookingUrl,
      brand_name: brandName,
      brand_short_name: brandShortName(brandName),
      call_center_phone: callCenterPhone,
      display_phone: callCenterPhone,
      first_name: deriveCustomerFirstName(selectedLead.name, selectedLead.email),
      opt_out_text: "",
      time_phrase: "a while back",
    })
    : "";

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;

    async function loadQueue() {
      setApiState("loading");
      setApiError(null);
      try {
        const res = await fetch("/api/speed-to-lead/summary", {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`summary_failed_${res.status}`);
        }
        const data = (await res.json()) as SpeedToLeadSummary;
        if (cancelled) return;
        applyQueue(data.queue);
        if (data.delivery) setDelivery(data.delivery);
        if (data.template) setSmsTemplate(data.template);
        setApiState("ready");
      } catch (err) {
        if (cancelled) return;
        setApiState("error");
        setApiError(err instanceof Error ? err.message : "summary_failed");
      }
    }

    void loadQueue();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    setStage("waiting");
    setApiError(null);
    setActionError(null);
    setActionMessage(null);
    try {
      if (!accessToken) throw new Error("missing_session");
      const fileBase64 = await fileToBase64(file);
      const res = await fetch("/api/speed-to-lead/wfy-import", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          filename: file.name,
          file_base64: fileBase64,
          confirm_upload: "SPEED_TO_LEAD_IMPORT",
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || `upload_failed_${res.status}`);
      }
      setImportSummary(data.import);
      applyQueue(data.queue);
      setApiState("ready");
      setStage("parsed");
    } catch (err) {
      setApiState("error");
      setApiError(err instanceof Error ? err.message : "upload_failed");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function handleDryRun() {
    setActionState("loading");
    setActionError(null);
    setActionMessage(null);
    try {
      if (!accessToken) throw new Error("missing_session");
      const res = await fetch("/api/speed-to-lead/dry-run", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          confirm_dry_run: "SPEED_TO_LEAD_DRY_RUN",
          lead_ids: selectedLeadIds,
          limit: selectedLeadIds.length || 10,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `dry_run_failed_${res.status}`);
      applyQueue(data.queue, { preserveSelection: true });
      setStage("previewed");
      setActionState("ready");
      setActionMessage(`Previewed ${data.dry_run?.dry_run_preview ?? 0} selected SMS.`);
    } catch (err) {
      setActionState("error");
      setActionError(err instanceof Error ? err.message : "dry_run_failed");
    }
  }

  async function handleManualSend() {
    if (selectedLeadIds.length === 0) return;
    const ok = window.confirm(`Send ${selectedLeadIds.length} Speed to Lead SMS now?`);
    if (!ok) return;
    setActionState("loading");
    setActionError(null);
    setActionMessage(null);
    try {
      if (!accessToken) throw new Error("missing_session");
      const res = await fetch("/api/speed-to-lead/manual-send", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          confirm_send: "SPEED_TO_LEAD_SEND_SELECTED",
          confirm_count: selectedLeadIds.length,
          lead_ids: selectedLeadIds,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `send_failed_${res.status}`);
      applyQueue(data.queue);
      setActionState("ready");
      setActionMessage(`Sent ${data.send?.sent ?? 0} SMS. Failed ${data.send?.failed ?? 0}.`);
    } catch (err) {
      setActionState("error");
      setActionError(err instanceof Error ? err.message : "send_failed");
    }
  }

  async function handleSuppressSelected() {
    if (selectedLeadIds.length === 0) return;
    const reason = window.prompt("Reason for suppressing selected leads");
    if (!reason) return;
    setActionState("loading");
    setActionError(null);
    setActionMessage(null);
    try {
      if (!accessToken) throw new Error("missing_session");
      const res = await fetch("/api/speed-to-lead/suppress", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          confirm_suppress: "SPEED_TO_LEAD_SUPPRESS_SELECTED",
          lead_ids: selectedLeadIds,
          reason,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `suppress_failed_${res.status}`);
      applyQueue(data.queue);
      setActionState("ready");
      setActionMessage(`Suppressed ${data.suppressed ?? 0} selected lead(s).`);
    } catch (err) {
      setActionState("error");
      setActionError(err instanceof Error ? err.message : "suppress_failed");
    }
  }

  function toggleLeadSelection(dbId: number | null) {
    if (!dbId) return;
    setSelectedLeadIds((current) => (
      current.includes(dbId)
        ? current.filter((id) => id !== dbId)
        : [...current, dbId]
    ));
  }

  function toggleAllSendable() {
    const ids = sendableLeads.map((lead) => lead.dbId).filter((id): id is number => Boolean(id));
    setSelectedLeadIds(allSendableSelected ? [] : ids);
  }

  function applyQueue(nextQueue: SpeedToLeadQueue, options: ApplyQueueOptions = {}) {
    setQueue(nextQueue);
    const mapped = nextQueue.leads.map(mapApiLead);
    setLeads(mapped);
    const readyIds = mapped
      .filter((lead) => lead.dbId && lead.status === "ready")
      .map((lead) => lead.dbId as number);
    setSelectedLeadIds((current) => {
      if (!options.preserveSelection) return readyIds;
      const readySet = new Set(readyIds);
      return current.filter((id) => readySet.has(id));
    });
    if (mapped.length > 0) {
      setSelectedId(mapped[0].id);
      setStage("parsed");
    }
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="slate">Speed to Lead</Badge>
            <Badge variant="green">Paid add-on active</Badge>
            <Badge variant={apiState === "error" ? "amber" : "green"}>{apiState === "ready" ? "Import API connected" : apiState === "error" ? "Import API unavailable" : "Import API loading"}</Badge>
            <Badge variant="slate">Owner only</Badge>
            <Badge variant={liveSendLocked ? "amber" : "green"}>{liveSendLocked ? "Manual preview only" : "Manual live open"}</Badge>
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-normal text-[#11133f] md:text-3xl">Speed to Lead</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Import the WebFindYou file, select eligible leads in the queue, preview the exact booking SMS, and suppress anything that should not be contacted.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            accept=".xls,.html,.htm"
            className="hidden"
            type="file"
            onChange={handleFileChange}
          />
          <Button className="w-full sm:w-fit" variant="secondary" disabled={uploading} onClick={() => fileInput.current?.click()}>
            <MiniIcon name="upload" />
            {uploading ? "Importing WFY file" : "Import WFY file"}
          </Button>
          <Link
            className="inline-flex h-9 w-full items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 sm:w-fit"
            to="/settings/brand"
          >
            Edit booking link
          </Link>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-6">
        <Metric label="Imported leads" value={String(counts.total)} />
        <Metric label="Ready" value={String(counts.sendable)} />
        <Metric label="Selected" value={String(selectedSendableCount)} />
        <Metric label="Blocked" value={String(counts.blocked)} />
        <Metric label="Sent" value={String(counts.sent)} />
        <Metric label="Booked" value={String(counts.booked)} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="border-b border-slate-100 bg-white">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle>Lead queue from WebFindYou</CardTitle>
                <p className="mt-1 text-xs text-slate-500">Ready rows are selectable. Blocked rows stay visible for audit.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="green">One SMS per lead</Badge>
                <Badge variant={stage === "waiting" ? "amber" : "green"}>{stage === "waiting" ? "Waiting file" : "Batch parsed"}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 p-0">
            <div className={`border-b px-4 py-3 ${liveSendLocked ? "border-amber-100 bg-amber-50/80" : "border-emerald-100 bg-emerald-50/80"}`}>
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <div className="text-sm font-semibold text-[#11133f]">
                    Selected: {selectedSendableCount} ready lead{selectedSendableCount === 1 ? "" : "s"}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    {liveSendLocked
                      ? "Live SMS locked. Preview or suppress selected rows here. Send is visible but disabled until Marc opens the live gate."
                      : "Manual live gate is open. Select only the leads you want to send now."}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                    disabled={sendableLeads.length === 0}
                    onClick={toggleAllSendable}
                  >
                    {allSendableSelected ? "Clear selection" : "Select sendable"}
                  </button>
                  <Button
                    className="w-full sm:w-fit"
                    variant={liveSendLocked ? "default" : "secondary"}
                    disabled={selectedSendableCount === 0 || actionState === "loading"}
                    onClick={handleDryRun}
                  >
                    <MiniIcon name="preview" />
                    Preview SMS
                  </Button>
                  <Button
                    className="w-full sm:w-fit"
                    variant={liveSendLocked ? "secondary" : "default"}
                    disabled={liveSendLocked || selectedSendableCount === 0 || actionState === "loading"}
                    title={liveSendLocked ? "Locked until Marc opens the live Speed to Lead send gate." : undefined}
                    onClick={handleManualSend}
                  >
                    <MiniIcon name="send" />
                    Send selected
                  </Button>
                  <Button
                    className="w-full sm:w-fit"
                    variant="secondary"
                    disabled={selectedSendableCount === 0 || actionState === "loading"}
                    onClick={handleSuppressSelected}
                  >
                    <MiniIcon name="x" />
                    Suppress selected
                  </Button>
                </div>
              </div>
            </div>

            {(apiError || actionError || actionMessage || importSummary) ? (
              <div className="space-y-2 px-4">
                {importSummary ? (
                  <div className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-950">
                    Parsed {importSummary.parsed} rows, imported {importSummary.imported}, blocked {importSummary.in_pipeline + (importSummary.existing_contact || 0) + importSummary.opted_out + importSummary.bad_phone + importSummary.duplicates}, ignored {importSummary.not_follow_up}.
                  </div>
                ) : null}
                {apiError ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
                    {apiError}
                  </div>
                ) : null}
                {actionError ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-950">
                    {actionError}
                  </div>
                ) : null}
                {actionMessage ? (
                  <div className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-950">
                    {actionMessage}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="overflow-x-auto px-4 pb-4">
              <table className="min-w-[720px] w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-normal text-slate-500">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        aria-label="Select all sendable leads"
                        checked={allSendableSelected}
                        disabled={sendableLeads.length === 0}
                        type="checkbox"
                        onChange={toggleAllSendable}
                      />
                    </th>
                    <th className="px-4 py-3">Lead</th>
                    <th className="px-3 py-3">Contact</th>
                    <th className="px-3 py-3">Attempts</th>
                    <th className="px-3 py-3">WFY status</th>
                    <th className="px-3 py-3">Murray status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayLeads.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                        No WebFindYou rows imported yet.
                      </td>
                    </tr>
                  ) : null}
                  {displayLeads.map((lead) => {
                    const selected = lead.id === selectedLead.id;
                    const sendable = Boolean(lead.dbId && lead.status === "ready");
                    const checked = Boolean(lead.dbId && selectedLeadIds.includes(lead.dbId));
                    return (
                      <tr key={lead.id} className={selected ? "bg-blue-50/60" : "bg-white"}>
                        <td className="px-4 py-3">
                          <input
                            aria-label={`Select ${lead.name}`}
                            checked={checked}
                            disabled={!sendable}
                            type="checkbox"
                            onChange={() => toggleLeadSelection(lead.dbId)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button className="text-left" onClick={() => setSelectedId(lead.id)}>
                            <span className="block font-semibold text-[#11133f]">{lead.name}</span>
                            <span className="mt-1 block text-xs text-slate-500">{lead.id} - last touch {lead.lastTouch}</span>
                          </button>
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-slate-700">{lead.phone}</div>
                          <div className="mt-1 text-xs text-slate-500">{lead.email}</div>
                          <div className="mt-1 text-xs text-slate-500">ZIP {lead.zip || "-"}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-600">{lead.attempts ?? "-"}</td>
                        <td className="px-3 py-3 text-slate-600">{lead.sourceStatus}</td>
                        <td className="px-3 py-3">
                          <LeadStatusBadge status={lead.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <Card className="overflow-hidden border-[#11133f]">
            <CardHeader className="bg-[#11133f] text-white">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-white">SMS preview</CardTitle>
                  <p className="mt-1 text-xs text-slate-300">{selectedLead.name} - {selectedLead.phone}</p>
                </div>
                <Badge variant={smsTemplate?.validation.ok === false ? "red" : stage === "previewed" ? "green" : "amber"}>
                  {smsTemplate?.validation.ok === false ? "Template blocked" : stage === "previewed" ? "Previewed" : "Locked"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 bg-[#11133f]">
              <div className="mx-auto w-full max-w-[310px] rounded-[1.75rem] border border-white/15 bg-slate-950 p-3 shadow-[0_18px_40px_rgba(0,0,0,0.22)]">
                <div className="mb-3 flex justify-center">
                  <div className="h-1.5 w-16 rounded-full bg-white/20" />
                </div>
                {hasBookingUrl ? (
                  <div className="rounded-2xl bg-white p-3 text-slate-950">
                    <div className="mb-2 text-xs font-semibold text-slate-500">{brandName}</div>
                    <p className="break-words text-sm leading-6">{smsBody}</p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-amber-50 p-3 text-amber-950">
                    <div className="mb-2 text-xs font-semibold text-amber-800">Booking URL missing</div>
                    <p className="text-sm leading-6">SMS preview locked until the tenant booking link is configured.</p>
                  </div>
                )}
              </div>
              <div className="rounded-md border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-200">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="font-semibold text-white">{smsTemplate?.name || "Speed to Lead booking SMS"}</div>
                    <div className="mt-0.5 text-slate-300">
                      {smsTemplate?.version ? `Tenant version v${smsTemplate.version}` : "Runtime default"} - {speedToLeadTemplateCode}
                    </div>
                  </div>
                  <a
                    className="inline-flex h-8 items-center justify-center rounded-md border border-white/15 px-3 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                    href={`/settings/templates?template=${speedToLeadTemplateCode}`}
                  >
                    Edit SMS template
                  </a>
                </div>
                {smsTemplate?.validation.ok === false ? (
                  <div className="mt-2 rounded-md bg-red-950/40 px-2 py-1 text-red-100">
                    Fix before sending: {smsTemplate.validation.errors.join(", ")}
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Batch and gates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <KeyValue label="Current file" value={batchName} />
                <KeyValue label="Batch id" value={batchId} />
                <KeyValue label="Mode" value={delivery.auto_mode_enabled ? "Automatic" : "Manual only"} />
                <KeyValue label="Manual send" value={delivery.manual_send_available ? "Enabled by Marc gate" : "Locked until Marc GO"} />
                <KeyValue label="SMS template" value={smsTemplate?.version ? `Tenant v${smsTemplate.version}` : "Runtime default"} />
              </div>
              <div className="space-y-2">
              {liveGates.map(([label, value]) => (
                <div key={label} className="flex items-start gap-2 rounded-md border border-slate-100 px-3 py-2">
                  <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                    <MiniIcon name="check" />
                  </span>
                  <div>
                    <div className="text-xs font-semibold text-slate-800">{label}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{value}</div>
                  </div>
                </div>
              ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-emerald-100">
            <CardHeader>
              <CardTitle>Booking path</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <PathStep title="1. SMS link" body="Customer opens the tenant booking page." />
              <PathStep title="2. Call fallback" body="The callback number remains visible." />
              <PathStep title="3. Calendar" body="The estimate appointment lands on the calendar." />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">{label}</div>
      <div className="mt-1 min-h-6 break-words text-sm font-semibold text-[#11133f]">{value}</div>
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">{label}</div>
      <div className="mt-1 rounded-md border border-slate-100 bg-white px-3 py-2 text-sm leading-5 text-slate-700">{value}</div>
    </div>
  );
}

function LeadStatusBadge({ status }: { status: LeadStatus }) {
  const meta = statusMeta(status);
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function PathStep({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-4">
      <div className="text-sm font-semibold text-emerald-950">{title}</div>
      <p className="mt-2 text-sm leading-6 text-emerald-900">{body}</p>
    </div>
  );
}

function MiniIcon({ name }: { name: "upload" | "preview" | "check" | "send" | "x" }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 2,
  };

  return (
    <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24">
      {name === "upload" ? (
        <>
          <path {...common} d="M12 16V4" />
          <path {...common} d="m7 9 5-5 5 5" />
          <path {...common} d="M5 20h14" />
        </>
      ) : null}
      {name === "preview" ? (
        <>
          <path {...common} d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" />
          <circle {...common} cx="12" cy="12" r="2.5" />
        </>
      ) : null}
      {name === "send" ? (
        <>
          <path {...common} d="m22 2-7 20-4-9-9-4Z" />
          <path {...common} d="M22 2 11 13" />
        </>
      ) : null}
      {name === "check" ? <path {...common} d="m5 12 4 4L19 6" /> : null}
      {name === "x" ? (
        <>
          <path {...common} d="M18 6 6 18" />
          <path {...common} d="m6 6 12 12" />
        </>
      ) : null}
    </svg>
  );
}
